import { useQuery, useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import {
  CourseListResponse,
  CourseLevelsResponse,
  QuestionsResponse,
  SaveProgressRequest,
} from '../interfaces/course';

async function fetchCourses(
  organisationId: number,
): Promise<CourseListResponse> {
  const response = await apiFetch(ENDPOINTS.COURSE_LIST(organisationId));

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch courses');
  }

  return response.json();
}

async function fetchLevels(courseId: number): Promise<CourseLevelsResponse> {
  const response = await apiFetch(ENDPOINTS.COURSE_LEVELS(courseId));

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch levels');
  }

  return response.json();
}

async function fetchQuestions(
  courseId: number,
  levelId: number,
): Promise<QuestionsResponse> {
  const response = await apiFetch(ENDPOINTS.LEVEL_QUESTIONS(courseId, levelId));

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch questions');
  }

  return response.json();
}

async function saveProgress(
  courseId: number,
  levelId: number,
  data: SaveProgressRequest,
): Promise<{ success: boolean }> {
  const response = await apiFetch(ENDPOINTS.SAVE_PROGRESS(courseId, levelId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to save progress');
  }

  return response.json();
}

export function useCourses(organisationId: number) {
  return useQuery({
    queryKey: ['course', 'list', organisationId],
    queryFn: () => fetchCourses(organisationId),
  });
}

export function useLevels(courseId: number) {
  return useQuery({
    queryKey: ['course', courseId, 'levels'],
    queryFn: () => fetchLevels(courseId),
  });
}

export function useQuestions(courseId: number, levelId: number) {
  return useQuery({
    queryKey: ['course', courseId, 'level', levelId, 'questions'],
    queryFn: () => fetchQuestions(courseId, levelId),
  });
}

export function useSaveProgress(courseId: number, levelId: number) {
  return useMutation({
    mutationFn: (data: SaveProgressRequest) =>
      saveProgress(courseId, levelId, data),
  });
}
